import { Button } from '@hakimo-ui/shared/ui-base';
import { Transition } from '@headlessui/react';
import { SpeakerWaveIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { default as rhToast } from 'react-hot-toast';
import { NotificationSound } from './NotificationSound';
type ToastType = 'info' | 'success' | 'warning' | 'error';

interface Options {
  type?: ToastType;
  audio?: boolean;
  audioFileName?: string;
  id?: string;
}

const toastVariants: Record<
  ToastType,
  {
    Icon: React.ComponentType<React.ComponentProps<'svg'>>;
    iconBg: string;
    iconCls: string;
    cardCls: string;
    overlayCls: string;
  }
> = {
  info: {
    Icon: InformationCircleIcon,
    iconBg: 'bg-primary-600 dark:bg-white',
    iconCls: 'text-white dark:text-primary-600',
    cardCls: 'border-primary-200 bg-primary-50 dark:border-primary-500',
    overlayCls: 'dark:bg-primary-600/20',
  },
  success: {
    Icon: CheckCircleIcon,
    iconBg: 'bg-green-500 dark:bg-white',
    iconCls: 'text-white dark:text-green-500',
    cardCls: 'border-green-200 bg-green-50 dark:border-green-500',
    overlayCls: 'dark:bg-green-800/20',
  },
  warning: {
    Icon: ExclamationCircleIcon,
    iconBg: 'bg-amber-500 dark:bg-white',
    iconCls: 'text-white dark:text-amber-500',
    cardCls: 'border-yellow-200 bg-yellow-50 dark:border-yellow-500',
    overlayCls: 'dark:bg-yellow-800/30',
  },
  error: {
    Icon: ExclamationTriangleIcon,
    iconBg: 'bg-alert-500 dark:bg-white',
    iconCls: 'text-white dark:text-alert-500',
    cardCls: 'border-alert-200 bg-alert-50 dark:border-alert-500',
    overlayCls: 'dark:bg-alert-700/20',
  },
};

export const dismissToast = (toastId: string) => rhToast.dismiss(toastId);

export const toast = (message: string, options?: Options) => {
  const type = options?.type ?? 'info';
  const { Icon, iconBg, iconCls, cardCls, overlayCls } = toastVariants[type];
  const isMultiLine = message.length > 40;

  rhToast.custom(
    (t) => (
      <Transition
        appear
        show={t.visible}
        className="w-fit max-w-sm"
        enter="transform transition ease-in-out duration-300"
        enterFrom="translate-y-full opacity-0"
        enterTo="translate-y-0 opacity-100"
        leave="transform transition ease-in-out duration-300"
        leaveFrom="translate-y-0 opacity-100"
        leaveTo="translate-y-full opacity-0"
      >
        <div
          className={clsx(
            'dark:bg-dark-surface pointer-events-auto w-fit overflow-hidden rounded-lg border shadow-lg',
            cardCls
          )}
        >
          <div className={clsx('p-2', overlayCls)}>
            <div className="flex items-center gap-1.5">
              <div
                className={clsx(
                  'flex flex-shrink-0 items-center justify-center rounded-lg',
                  isMultiLine ? 'h-8 w-8' : 'h-6 w-6',
                  iconBg
                )}
                aria-hidden="true"
              >
                <Icon
                  className={clsx(isMultiLine ? 'h-6 w-6' : 'h-5 w-5', iconCls)}
                />
              </div>
              <p className="dark:text-dark-text text-sm text-gray-900">
                {message}
              </p>
              {options?.audio && (
                <div className="dark:text-dark-text flex items-center opacity-70">
                  <SpeakerWaveIcon className="h-4 w-4" />
                  <NotificationSound
                    enabled={t.visible && options.audio}
                    audioFileName={options.audioFileName}
                  />
                </div>
              )}
              <Button
                variant="icon"
                className="!p-0 hover:bg-transparent dark:hover:bg-transparent"
                onClick={() => rhToast.dismiss(t.id)}
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </Button>
            </div>
          </div>
        </div>
      </Transition>
    ),
    { duration: 3000, id: options?.id }
  );
};
